import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

export function useBrowserFullscreen<T extends HTMLElement = HTMLElement>(): {
  exitFullscreen: () => void;
  isFullscreen: boolean;
  ref: RefObject<null | T>;
  requestFullscreen: () => void;
  toggleFullscreen: () => void;
} {
  const ref = useRef<null | T>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const sync = useCallback(() => {
    setIsFullscreen(ref.current != null && isElementFullscreen(ref.current));
  }, []);

  useEffect(() => {
    const doc = document;
    doc.addEventListener("fullscreenchange", sync);
    doc.addEventListener("webkitfullscreenchange", sync);
    return () => {
      doc.removeEventListener("fullscreenchange", sync);
      doc.removeEventListener("webkitfullscreenchange", sync);
    };
  }, [sync]);

  const requestFullscreen = useCallback(() => {
    const el = ref.current;
    if (el) requestElementFullscreen(el);
  }, []);

  const exitFullscreen = useCallback(() => {
    exitElementFullscreen();
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (isElementFullscreen(el)) exitElementFullscreen();
    else requestElementFullscreen(el);
  }, []);

  return { exitFullscreen, isFullscreen, ref, requestFullscreen, toggleFullscreen };
}

function exitElementFullscreen() {
  if (document.exitFullscreen) return document.exitFullscreen();
  return Promise.resolve();
}

function isElementFullscreen(el: Element) {
  return document.fullscreenElement === el;
}

function requestElementFullscreen(el: HTMLElement) {
  if (el.requestFullscreen) return el.requestFullscreen();
  return Promise.resolve();
}
